import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Alert, AlertTitle, Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import GridOnIcon from "@mui/icons-material/GridOn";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ClearIcon from "@mui/icons-material/Clear";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import Header from "./Header";
import CollapsiblePanel from "./CollapsiblePanel";
import History from "./History";
import Parameters from "./Parameters";
import Splines from "./Splines";
import DataInput from "./DataInput";
import { getInitialState } from "./DefaultParameters";

const createEmptyPredictRows = (count = 5) =>
    Array.from({ length: count }, () => ({ sampleId: "", response1: "", response2: "" }));

const loadStoredCalibrationResponse = () => {
    try {
        const raw = localStorage.getItem("microslideCalibrationResponse");
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

// Splines persisted by CalibrationLayout alongside A0..A3 so Predict can
// push Resp1/Resp2 through the SAME G1/G2 the calibration used. Without
// these the math returns U90-289 (CP_INVALID_SPLINE_RANGE_2_ERR) for any
// DM assay and dConcentration comes back null.
const loadStoredCalibrationSplines = () => {
    try {
        const raw = localStorage.getItem("microslideCalibrationSplines");
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

// Prediction tab. Calls POST /api/v1/microslide/predict.
//
// Reads an optional calibrationResponse from react-router location state (set
// by CalibrationLayout after a successful Calibrate) with a localStorage
// fallback for refresh persistence. When present, the four A0-A3 coefficients
// are written into `parameters` so the predict payload picks them up without
// the user manually copying them from the Calibration tab.
export default function PredictionLayout() {
    const [resultsCollapsed, setResultsCollapsed] = useState(true);
    const [dataCollapsed, setDataCollapsed] = useState(false);
    const [splinesCollapsed, setSplinesCollapsed] = useState(true);
    const [paramsCollapsed, setParamsCollapsed] = useState(true);
    const [chartCollapsed, setChartCollapsed] = useState(true);

    const [rows, setRows] = useState(createEmptyPredictRows(5));
    const [parameters, setParameters] = useState(getInitialState());
    const [splines, setSplines] = useState(() => {
        const stored = loadStoredCalibrationSplines();
        return stored && Object.keys(stored).length > 0
            ? stored
            : { preprocessing: null, g1: null, g2: null, g3: null, g4: null };
    });

    const location = useLocation();
    const chartContainerRef = useRef(null);

    // Held as state so clearCalibration() can null it out without bouncing
    // through the router (window.history.replaceState would not update
    // useLocation()'s view of route state).
    const [calibrationResponse, setCalibrationResponse] = useState(() => {
        return loadStoredCalibrationResponse();
    });

    // Declared above the route-state useEffect because that effect resets
    // resultDataMap on a fresh Calibrate hand-off; const TDZ would otherwise
    // throw at effect-callback time.
    const [resultDataMap, setResultDataMap] = useState(() => {
        const saved = sessionStorage.getItem("microslideHistory_predict");
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        const fromRoute = location?.state?.calibrationResponse;
        if (fromRoute && Object.keys(fromRoute).length > 0) {
            setCalibrationResponse(fromRoute);
            localStorage.setItem(
                "microslideCalibrationResponse",
                JSON.stringify(fromRoute)
            );

            // A fresh Calibrate click invalidates any in-flight predict
            // session: A0..A3 just changed, so the existing chart / history /
            // sample rows would be plotting concentrations against an old
            // calibration. Reset the prediction-side UI so the user always
            // starts from a clean slate after each successful Calibrate.
            // (Splines + A0..A3 still carry over via the hydration blocks
            // below - only the user-facing predict inputs/outputs get wiped.)
            setRows(createEmptyPredictRows(5));
            setResultDataMap({});
            sessionStorage.removeItem("microslideHistory_predict");
            setResultsCollapsed(true);
            setChartCollapsed(true);
            setDataCollapsed(false);
        }
        const splinesFromRoute = location?.state?.calibrationSplines;
        if (splinesFromRoute && Object.keys(splinesFromRoute).length > 0) {
            setSplines(splinesFromRoute);
            localStorage.setItem(
                "microslideCalibrationSplines",
                JSON.stringify(splinesFromRoute)
            );
        }
    }, [location?.state]);

    // The predict payload reads fCalInterceptA0..fCalBlankCorrectionFactorA3
    // from `parameters` (see MicroSlideApi.buildPredictPayloadFor), so writing
    // the active calibration here is enough - the next Predict call uses it.
    useEffect(() => {
        if (!calibrationResponse) return;
        setParameters(prev => ({
            ...prev,
            fCalInterceptA0: String(calibrationResponse.fCalInterceptA0 ?? prev.fCalInterceptA0 ?? 0),
            fCalSlopeA1: String(calibrationResponse.fCalSlopeA1 ?? prev.fCalSlopeA1 ?? 1),
            fCalCurvatureA2: String(calibrationResponse.fCalCurvatureA2 ?? prev.fCalCurvatureA2 ?? 0),
            fCalBlankCorrectionFactorA3: String(calibrationResponse.fCalBlankCorrectionFactorA3 ?? prev.fCalBlankCorrectionFactorA3 ?? 0)
        }));
    }, [calibrationResponse]);

    useEffect(() => {
        if (resultDataMap && Object.keys(resultDataMap).length > 0) {
            setResultsCollapsed(false);
            setChartCollapsed(false);
        }
    }, [resultDataMap]);

    const latestPredictions = useMemo(() => {
        const runs = Object.values(resultDataMap || {});
        if (runs.length === 0) return [];
        const latest = runs[runs.length - 1];
        const preds = latest?.result?.predictions;
        return Array.isArray(preds) ? preds : [];
    }, [resultDataMap]);

    const chartOptions = useMemo(() => ({
        chart: { type: "scatter", height: 360, zoomType: "xy" },
        title: { text: "Predicted Concentration per Sample" },
        xAxis: {
            title: { text: "Sample" },
            categories: latestPredictions.map((p, i) => p.sampleId || String(i + 1))
        },
        yAxis: { title: { text: "Concentration" } },
        legend: { enabled: false },
        credits: { enabled: false },
        tooltip: {
            useHTML: true,
            formatter: function () {
                const p = latestPredictions[this.point.index] || {};
                return (
                    `<b>${p.sampleId || `Sample ${this.point.index + 1}`}</b><br/>` +
                    `Resp1: ${p.response1 ?? "-"}<br/>` +
                    `Resp2: ${p.response2 ?? "-"}<br/>` +
                    `Conc: <b>${p.concentration ?? "-"}</b><br/>` +
                    (p.conditionCode ? `Cond: ${p.conditionCode}` : "")
                );
            }
        },
        series: [{
            name: "Concentration",
            data: latestPredictions.map((p, i) => ({
                x: i,
                y: Number(p.concentration) || 0,
                marker: {
                    fillColor: p.conditionCode ? "#d32f2f" : "#1976d2",
                    radius: 5
                }
            }))
        }]
    }), [latestPredictions]);

    const clearCalibration = () => {
        localStorage.removeItem("microslideCalibrationResponse");
        setCalibrationResponse(null);
        setParameters(prev => ({
            ...prev,
            fCalInterceptA0: "0",
            fCalSlopeA1: "1",
            fCalCurvatureA2: "0",
            fCalBlankCorrectionFactorA3: "0"
        }));
        // Drop the route state copy so a tab-switch back to /microslide does
        // not re-apply the cleared calibration via the location useEffect.
        window.history.replaceState({}, "");
    };

    const exportToExcel = () => {
        if (latestPredictions.length === 0) {
            alert("Run Predict first - no predictions to export.");
            return;
        }
        const rowsForSheet = latestPredictions.map((p, i) => ({
            Sample: p.sampleId || i + 1,
            Response1: p.response1 ?? "",
            Response2: p.response2 ?? "",
            Concentration: p.concentration ?? "",
            ConditionCode: p.conditionCode ?? "",
            Error: p.error ?? ""
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(rowsForSheet);
        XLSX.utils.book_append_sheet(wb, ws, "Predictions");

        const calRows = [
            ["A0 (Intercept)", parameters.fCalInterceptA0 ?? ""],
            ["A1 (Slope)", parameters.fCalSlopeA1 ?? ""],
            ["A2 (Curvature)", parameters.fCalCurvatureA2 ?? ""],
            ["A3 (Blank Correction)", parameters.fCalBlankCorrectionFactorA3 ?? ""]
        ];
        const calSheet = XLSX.utils.aoa_to_sheet([
            ["Coefficient", "Value"],
            ...calRows
        ]);
        XLSX.utils.book_append_sheet(wb, calSheet, "Calibration");

        const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        XLSX.writeFile(wb, `MicroSlide_Predictions_${stamp}.xlsx`);
    };

    const exportToPdf = async () => {
        if (latestPredictions.length === 0) {
            alert("Run Predict first - no predictions to export.");
            return;
        }
        const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
        doc.setFontSize(14);
        doc.text("MicroSlide Prediction Report", 40, 40);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 58);

        doc.text("Active Calibration:", 40, 78);
        autoTable(doc, {
            startY: 84,
            head: [["Coefficient", "Value"]],
            body: [
                ["A0 (Intercept)", String(parameters.fCalInterceptA0 ?? "")],
                ["A1 (Slope)", String(parameters.fCalSlopeA1 ?? "")],
                ["A2 (Curvature)", String(parameters.fCalCurvatureA2 ?? "")],
                ["A3 (Blank Correction)", String(parameters.fCalBlankCorrectionFactorA3 ?? "")]
            ],
            styles: { fontSize: 9 },
            margin: { left: 40, right: 40 }
        });

        // html2canvas is dynamically imported because it is a transitive
        // dep of jspdf rather than a direct one; importing on demand keeps
        // it out of the main bundle.
        try {
            if (chartContainerRef.current) {
                const html2canvas = (await import("html2canvas")).default;
                const canvas = await html2canvas(chartContainerRef.current, {
                    backgroundColor: "#ffffff",
                    scale: 2
                });
                const imgData = canvas.toDataURL("image/png");
                const y = (doc.lastAutoTable?.finalY ?? 120) + 16;
                doc.addImage(imgData, "PNG", 40, y, 520, 240);
            }
        } catch (err) {
            console.warn("Chart embed in PDF skipped:", err);
        }

        // Predictions table on a fresh page so it does not clip the chart.
        doc.addPage();
        doc.setFontSize(12);
        doc.text("Predictions", 40, 40);
        autoTable(doc, {
            startY: 50,
            head: [["Sample", "Resp1", "Resp2", "Concentration", "Cond", "Error"]],
            body: latestPredictions.map((p, i) => [
                p.sampleId || i + 1,
                p.response1 ?? "",
                p.response2 ?? "",
                p.concentration ?? "",
                p.conditionCode ?? "",
                p.error ?? ""
            ]),
            styles: { fontSize: 9 },
            margin: { left: 40, right: 40 }
        });

        const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        doc.save(`MicroSlide_Predictions_${stamp}.pdf`);
    };

    const hasCalibration = !!calibrationResponse;

    return (
        <div>
            <Header
                mode="predict"
                rows={rows}
                parameters={parameters}
                setParameters={setParameters}
                extras={{ splines }}
                setResultDataMap={setResultDataMap}
                onRun={() => setResultsCollapsed(false)}
            />

            <Box display="flex" flexDirection="column" padding={2}>

                {hasCalibration && (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        action={
                            <Button
                                size="small"
                                color="inherit"
                                startIcon={<ClearIcon />}
                                onClick={clearCalibration}
                            >
                                Clear
                            </Button>
                        }
                    >
                        <AlertTitle>Calibration active - A0..A3 auto-applied to Predict</AlertTitle>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            <Typography variant="body2">A0 = {parameters.fCalInterceptA0}</Typography>
                            <Typography variant="body2">A1 = {parameters.fCalSlopeA1}</Typography>
                            <Typography variant="body2">A2 = {parameters.fCalCurvatureA2}</Typography>
                            <Typography variant="body2">A3 = {parameters.fCalBlankCorrectionFactorA3}</Typography>
                        </Stack>
                    </Alert>
                )}

                <CollapsiblePanel
                    title="Results"
                    collapsed={resultsCollapsed}
                    onToggle={() => setResultsCollapsed(!resultsCollapsed)}
                >
                    <History
                        resultDataMap={resultDataMap}
                        resultFields={["Sample Count", "First Concentration", "Last Concentration"]}
                    />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Prediction Chart"
                    collapsed={chartCollapsed}
                    onToggle={() => setChartCollapsed(!chartCollapsed)}
                    headerExtras={
                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small" variant="outlined" startIcon={<GridOnIcon />}
                                onClick={(e) => { e.stopPropagation(); exportToExcel(); }}
                                disabled={latestPredictions.length === 0}
                                sx={{
                                    color: "#ffffff",
                                    borderColor: "rgba(255,255,255,0.7)",
                                    "&:hover": {
                                        borderColor: "#ffffff",
                                        backgroundColor: "rgba(255,255,255,0.12)"
                                    },
                                    "&.Mui-disabled": {
                                        color: "rgba(255,255,255,0.45)",
                                        borderColor: "rgba(255,255,255,0.3)"
                                    }
                                }}
                            >
                                Excel
                            </Button>
                            <Button
                                size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
                                onClick={(e) => { e.stopPropagation(); exportToPdf(); }}
                                disabled={latestPredictions.length === 0}
                                sx={{
                                    color: "#ffffff",
                                    borderColor: "rgba(255,255,255,0.7)",
                                    "&:hover": {
                                        borderColor: "#ffffff",
                                        backgroundColor: "rgba(255,255,255,0.12)"
                                    },
                                    "&.Mui-disabled": {
                                        color: "rgba(255,255,255,0.45)",
                                        borderColor: "rgba(255,255,255,0.3)"
                                    }
                                }}
                            >
                                PDF
                            </Button>
                        </Stack>
                    }
                >
                    {latestPredictions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Run Predict to populate the chart and enable Excel / PDF export.
                        </Typography>
                    ) : (
                        <div ref={chartContainerRef}>
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </div>
                    )}
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Sample Data (one row per sample)"
                    collapsed={dataCollapsed}
                    onToggle={() => setDataCollapsed(!dataCollapsed)}
                >
                    <DataInput
                        columns={[
                            { key: "sampleId", label: "Sample ID" },
                            { key: "response1", label: "Response 1" },
                            { key: "response2", label: "Response 2" }
                        ]}
                        rows={rows}
                        setRows={setRows}
                        placeholder={"SampleID\tResp1\tResp2\n1\t0.5\t0.25\n2\t0.7\t0.35\n..."}
                    />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Splines"
                    collapsed={splinesCollapsed}
                    onToggle={() => setSplinesCollapsed(!splinesCollapsed)}
                >
                    <Splines splines={splines} setSplines={setSplines} />
                </CollapsiblePanel>

                <CollapsiblePanel
                    title="Assay Parameters"
                    collapsed={paramsCollapsed}
                    onToggle={() => setParamsCollapsed(!paramsCollapsed)}
                >
                    <Parameters parameters={parameters} setParameters={setParameters} />
                </CollapsiblePanel>
            </Box>
        </div>
    );
}
